import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Code2, Layers, Wrench } from "lucide-react";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_TUTTIENGLISH } from "../../utils/const/string-const";

// Replace with your photo when ready: import profilePhoto from "../../assets/profile-developer.jpg";
import profilePhoto from "../../assets/profile_picture.png";

const DEVELOPER = {
    fullName: "Jose Natalio Tapia Marca",
    companyEmail: "tapia.jose.natalio@gmail.com",
    phone: "+59176697125",
    email: "tapia.jose.natalio@gmail.com",
    addressLine1: "ZONA PLAN 3000 BARRIO CRISTAL CALLE SN",
    cityRegion: "Santa Cruz - Bolivia",
    zipCode: "0000",
};

export default function ProfilePage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [photoSrc, setPhotoSrc] = useState(profilePhoto);

    const languages = [
        { value: "en", label: "English", flag: "🇺🇸" },
        { value: "es", label: "Español", flag: "🇪🇸" },
        { value: "pt", label: "Português", flag: "🇧🇷" },
    ];

    const services = [
        { icon: Code2, textKey: "dev_profile_service_1" },
        { icon: Layers, textKey: "dev_profile_service_2" },
        { icon: Wrench, textKey: "dev_profile_service_3" },
    ];

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_TUTTIENGLISH);
        if (language) {
            i18n.changeLanguage(language);
            setSelectedLanguage(language);
        } else {
            i18n.changeLanguage("en");
            setSelectedLanguage("en");
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isLanguageDropdownOpen &&
                !event.target.closest(".language-dropdown")
            ) {
                setIsLanguageDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isLanguageDropdownOpen]);

    const LanguageDropdown = () => (
        <div className="relative language-dropdown">
            <button
                type="button"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition border border-transparent hover:border-indigo-100"
                aria-expanded={isLanguageDropdownOpen}
                aria-haspopup="listbox"
            >
                <span className="text-xl" aria-hidden>
                    {languages.find((lang) => lang.value === selectedLanguage)?.flag}
                </span>
                <span className="text-slate-700 text-sm font-medium hidden sm:inline">
                    {languages.find((lang) => lang.value === selectedLanguage)?.label}
                </span>
                <svg
                    className={`w-4 h-4 text-slate-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isLanguageDropdownOpen && (
                <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50"
                    role="listbox"
                >
                    {languages.map((language) => (
                        <button
                            type="button"
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsLanguageDropdownOpen(false);
                                i18n.changeLanguage(language.value);
                                localStorage.setItem(STRING_LANGUAGE_TUTTIENGLISH, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-indigo-50 transition text-left ${selectedLanguage === language.value ? "bg-indigo-50 text-indigo-800" : "text-slate-700"}`}
                            role="option"
                            aria-selected={selectedLanguage === language.value}
                        >
                            <span className="text-xl" aria-hidden>{language.flag}</span>
                            <span className="font-medium">{language.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const currentYear = new Date().getFullYear();

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-indigo-200 selection:text-indigo-950">
            <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
                <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />
                <div className="absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-violet-400/15 blur-3xl" />
            </div>

            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3.5 gap-3">
                        <button
                            type="button"
                            onClick={() => history.push("/landing")}
                            className="text-left font-bold text-xl sm:text-2xl tracking-tight text-indigo-900 hover:text-indigo-700 transition shrink-0"
                        >
                            {t("te_brand")}
                        </button>

                        <div className="hidden sm:flex items-center gap-4 text-sm">
                            <button
                                type="button"
                                onClick={() => history.push("/landing")}
                                className="text-slate-600 hover:text-indigo-600 font-medium transition"
                            >
                                {t("dev_profile_back")}
                            </button>
                        </div>

                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            <button
                                type="button"
                                className="sm:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                                onClick={() => setMobileNavOpen((o) => !o)}
                                aria-expanded={mobileNavOpen}
                                aria-label="Menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileNavOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                            <div className="hidden sm:block">
                                <LanguageDropdown />
                            </div>
                            <button
                                type="button"
                                onClick={() => history.push("/login")}
                                className="bg-indigo-600 text-white px-3 sm:px-5 py-2 rounded-xl hover:bg-indigo-700 transition font-semibold text-sm shadow-md shadow-indigo-600/20 whitespace-nowrap"
                            >
                                {t("label_iniciar_sesion")}
                            </button>
                        </div>
                    </div>

                    {mobileNavOpen && (
                        <div className="sm:hidden border-t border-slate-100 py-3 flex flex-col gap-2 px-1">
                            <button
                                type="button"
                                onClick={() => {
                                    history.push("/landing");
                                    setMobileNavOpen(false);
                                }}
                                className="text-left px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
                            >
                                {t("dev_profile_back")}
                            </button>
                        </div>
                    )}

                    <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center">
                        <LanguageDropdown />
                    </div>
                </div>
            </header>

            <main className="relative px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24">
                <div className="max-w-4xl mx-auto">
                    <span className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200/60">
                        <span aria-hidden>💻</span>
                        {t("dev_profile_badge")}
                    </span>

                    <div className="relative rounded-3xl border border-slate-200/80 bg-white shadow-xl shadow-slate-900/5 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-violet-500/10 pointer-events-none" />

                        <div className="relative p-6 sm:p-10">
                            <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-center md:items-start">
                                <div className="shrink-0">
                                    <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-3xl overflow-hidden border-4 border-white shadow-lg shadow-indigo-900/10 ring-2 ring-indigo-100 bg-indigo-50">
                                        <img
                                            src={photoSrc}
                                            alt={DEVELOPER.fullName}
                                            className="w-full h-full object-cover"
                                            onError={() => setPhotoSrc(profilePhoto)}
                                        />
                                    </div>
                                </div>

                                <div className="flex-1 text-center md:text-left min-w-0">
                                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                                        {DEVELOPER.fullName}
                                    </h1>
                                    <p className="mt-2 text-lg sm:text-xl font-semibold text-indigo-700">
                                        {t("dev_profile_role")}
                                    </p>
                                    <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">
                                        {t("dev_profile_subtitle")}
                                    </p>
                                    <p className="mt-4 text-sm sm:text-base text-slate-500 leading-relaxed max-w-2xl">
                                        {t("dev_profile_about")}
                                    </p>

                                    <div className="mt-6 flex flex-col sm:flex-row flex-wrap justify-center md:justify-start gap-3">
                                        <a
                                            href={`mailto:${DEVELOPER.email}`}
                                            className="inline-flex justify-center items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/25"
                                        >
                                            <Mail className="w-4 h-4" aria-hidden />
                                            {t("dev_profile_cta_email")}
                                        </a>
                                        <a
                                            href={`tel:${DEVELOPER.phone.replace(/\s/g, "")}`}
                                            className="inline-flex justify-center items-center gap-2 bg-white text-slate-800 px-6 py-3 rounded-xl text-sm font-semibold border-2 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition"
                                        >
                                            <Phone className="w-4 h-4 text-indigo-600" aria-hidden />
                                            {t("dev_profile_cta_phone")}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10 pt-8 border-t border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 mb-4">
                                    {t("dev_profile_services_title")}
                                </h2>
                                <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {services.map(({ icon: Icon, textKey }) => (
                                        <li
                                            key={textKey}
                                            className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100"
                                        >
                                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
                                                <Icon className="w-5 h-5" aria-hidden />
                                            </span>
                                            <span className="text-sm font-medium text-slate-700 pt-1.5">
                                                {t(textKey)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-10 pt-8 border-t border-slate-100">
                                <h2 className="text-lg font-bold text-slate-900 mb-5">
                                    {t("dev_profile_contact_title")}
                                </h2>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <ContactCard
                                        icon={Mail}
                                        label={t("dev_profile_company_email")}
                                        value={DEVELOPER.companyEmail}
                                        href={`mailto:${DEVELOPER.companyEmail}`}
                                    />
                                    <ContactCard
                                        icon={Mail}
                                        label={t("dev_profile_email")}
                                        value={DEVELOPER.email}
                                        href={`mailto:${DEVELOPER.email}`}
                                    />
                                    <ContactCard
                                        icon={Phone}
                                        label={t("dev_profile_phone")}
                                        value={DEVELOPER.phone}
                                        href={`tel:${DEVELOPER.phone.replace(/\s/g, "")}`}
                                    />
                                    <ContactCard
                                        icon={MapPin}
                                        label={t("dev_profile_address")}
                                        value={
                                            <>
                                                {DEVELOPER.addressLine1}
                                                <br />
                                                {DEVELOPER.cityRegion}
                                                <br />
                                                <span className="text-slate-500">
                                                    {t("dev_profile_zip")}: {DEVELOPER.zipCode}
                                                </span>
                                            </>
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <footer className="relative bg-slate-950 text-slate-300 py-10 px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto text-center">
                    <p className="text-white font-bold text-lg">{t("te_brand")}</p>
                    <p className="text-sm text-slate-400 mt-2">{t("te_footer_line")}</p>
                    <p className="text-sm text-slate-500 mt-6">
                        © {currentYear} {DEVELOPER.fullName}. {t("te_footer_rights")}
                    </p>
                </div>
            </footer>
        </div>
    );
}

function ContactCard({ icon: Icon, label, value, href }) {
    const content = (
        <div className="flex gap-3 p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white hover:border-indigo-200 hover:shadow-md hover:shadow-indigo-900/5 transition h-full">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                <Icon className="w-5 h-5" aria-hidden />
            </span>
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-sm font-medium text-slate-800 break-words">{value}</p>
            </div>
        </div>
    );

    if (href) {
        return (
            <a href={href} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-2xl">
                {content}
            </a>
        );
    }

    return content;
}
